import { useBooleanChoices } from 'hooks/useBooleanChoices';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { RaRecord } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import {
  AutocompleteInput,
  AutocompleteMultiselectInput,
  TextInput,
} from 'shared/react-admin/Inputs';
import { ReferenceArrayInput, ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { cleanEmpty } from 'utils/cleanEmpty';

export const GatewayRulesFiltersForm = () => {
  const translate = useTypedTranslate();
  const booleanChoices = useBooleanChoices({ useNumericKeys: true });
  const { watch, resetField } = useFormContext();

  const [merchant, provider, gateway] = watch(['merchant', 'provider', 'gateway']);

  return (
    <>
      <TextInput
        helperText={false}
        label={translate('app.filter.name')}
        name="name"
        resettable
        source="name"
        sx={{ minWidth: 0 }}
        variant="outlined"
      />
      <TextInput
        alwaysOn
        helperText={false}
        key="id"
        label={translate('app.filter.ruleId')}
        resettable
        source="id"
        sx={{ minWidth: 0 }}
        variant="outlined"
      />
      <AutocompleteInput
        choices={booleanChoices}
        helperText={false}
        label={translate('app.filter.active')}
        name="active"
        optionText="name"
        source="active"
        sx={{ minWidth: 0 }}
        variant="outlined"
      />
      <ReferenceInput name="merchant" perPage={100} reference="merchants" source="merchant">
        <AutocompleteInput
          filterToQuery={(searchText: string) => ({ name: searchText })}
          helperText={false}
          label={translate('app.filter.merchant')}
          onChange={() => {
            resetField('provider');
            resetField('gateway');
            resetField('method');
          }}
          optionText="name"
          sx={{ minWidth: 0 }}
          variant="outlined"
        />
      </ReferenceInput>
      <ReferenceInput
        filter={cleanEmpty({ merchantAccounts: { merchants: [merchant] } })}
        name="provider"
        perPage={100}
        reference="providers"
        sort={{ field: 'name', order: 'ASC' }}
        source="provider"
      >
        <AutocompleteInput
          filterToQuery={(searchText: string) => ({ name: searchText })}
          helperText={false}
          label={translate('app.filter.provider')}
          onChange={() => {
            resetField('gateway');
            resetField('method');
          }}
          optionText="name"
          sx={{ minWidth: 0 }}
          variant="outlined"
        />
      </ReferenceInput>
      <ReferenceArrayInput
        filter={cleanEmpty({ merchantAccounts: { merchants: [merchant] }, provider })}
        perPage={100}
        reference="gateways"
        source="gateway"
      >
        <AutocompleteMultiselectInput
          filterToQuery={(searchText: string) => ({ name: searchText })}
          helperText={false}
          label={translate('app.filter.gateways')}
          name="gateway"
          onChange={() => {
            resetField('method');
          }}
          optionText="name"
          sx={{ minWidth: 0 }}
        />
      </ReferenceArrayInput>
      <ReferenceInput
        filter={cleanEmpty({ merchant: [merchant], provider: [provider], gateway })}
        name="method"
        perPage={100}
        reference="methods"
        source="method"
      >
        <AutocompleteInput
          filterToQuery={(searchText: string) => ({ name: searchText })}
          helperText={false}
          label={translate('app.filter.method')}
          optionText="name"
          sx={{ minWidth: 0 }}
          variant="outlined"
        />
      </ReferenceInput>
      <TextInput
        alwaysOn
        helperText={false}
        key="subagent-id"
        label={translate('app.merchantAccounts.subagentId')}
        resettable
        source="mappings.mappingItems.subagent"
        sx={{ minWidth: 0 }}
        variant="outlined"
      />
      <TextInput
        helperText={false}
        label={translate('app.filter.clientId')}
        name="conditionGroup.expressions.clientId"
        resettable
        source="conditionGroup.expressions.clientId"
        sx={{ minWidth: 0 }}
        variant="outlined"
      />
      <ReferenceArrayInput perPage={100} reference="countries" source="country">
        <AutocompleteMultiselectInput
          helperText={false}
          label={translate('app.filter.countries')}
          name="country"
          optionText="name"
          size="small"
        />
      </ReferenceArrayInput>
      <ReferenceArrayInput
        filter={{ active: true }}
        perPage={1000}
        reference="currencies"
        sort={{ field: 'id', order: 'ASC' }}
        source="currency"
      >
        <AutocompleteMultiselectInput
          filterToQuery={(searchText: string) => ({ currency: searchText })}
          getOptionLabel={(record: RaRecord) => `${record?.alphaCode}, ${record?.name}`}
          helperText={false}
          label={translate('app.filter.currencies')}
          name="currency"
          size="small"
        />
      </ReferenceArrayInput>
    </>
  );
};
